"use client";

/**
 * AnalyticsProvider — client component that initializes analytics,
 * captures UTM params from the URL, and tracks page views on navigation.
 *
 * This component must be placed inside the `<body>` tag in the root layout.
 * It renders no visible UI.
 */

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { analytics } from "@/lib/analytics";
import {
  extractUtmParams,
  hasUtmParams,
  persistUtmParams,
  getPersistedUtmParams,
  utmToEventContext,
} from "@/lib/analytics/utm";
import { hasConsent } from "@/lib/consent";

function usePageViewTracking() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Track previous pathname to avoid double-firing
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip if same path (React strict mode double-mount)
    if (prevPathRef.current === pathname) return;
    prevPathRef.current = pathname;

    // Collect UTM context
    const urlUtm = searchParams
      ? extractUtmParams(searchParams)
      : {};
    const persistedUtm = getPersistedUtmParams();
    const utmContext = utmToEventContext({ ...persistedUtm, ...urlUtm });

    // If new UTM params came in from URL, persist them
    if (hasUtmParams(urlUtm)) {
      persistUtmParams(urlUtm);
    }

    // Determine device category
    const width =
      typeof window !== "undefined" ? window.innerWidth : 1024;
    const deviceCategory =
      width < 768 ? "mobile" : width < 1024 ? "tablet" : "desktop";

    // Detect locale from path
    const localeMatch = pathname.match(/^\/([a-z]{2})\//);
    const locale = localeMatch?.[1];

    analytics.page({
      path: pathname,
      title: typeof document !== "undefined" ? document.title : undefined,
      locale,
      referrer:
        typeof document !== "undefined" ? document.referrer : undefined,
      deviceCategory,
      ...utmContext,
    });
  }, [pathname, searchParams]);
}

export default function AnalyticsProvider() {
  usePageViewTracking();

  const [analyticsConsent, setAnalyticsConsent] = useState(false);

  useEffect(() => {
    // Check initial consent state
    setAnalyticsConsent(hasConsent("analytics"));

    // Listen for consent updates
    function onConsentUpdated() {
      setAnalyticsConsent(hasConsent("analytics"));
    }
    window.addEventListener("consent-updated", onConsentUpdated);
    return () => window.removeEventListener("consent-updated", onConsentUpdated);
  }, []);

  // Only render PostHog / GA4 script tags if user has consented
  if (!analyticsConsent) return null;

  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const posthogHost =
    process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";
  const ga4Id = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;

  return (
    <>
      {posthogKey && (
        <script
          dangerouslySetInnerHTML={{
            __html: `
              !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
              posthog.init('${posthogKey}',{api_host:'${posthogHost}',person_profiles:'identified_only',capture_pageview:false,capture_pageleave:true});
            `,
          }}
        />
      )}
      {ga4Id && (
        <>
          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${ga4Id}`}
          />
          <script
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer=window.dataLayer||[];
                function gtag(){dataLayer.push(arguments);}
                gtag('js',new Date());
                gtag('config','${ga4Id}',{send_page_view:false});
              `,
            }}
          />
        </>
      )}
    </>
  );
}
